// Admin billing API surface. Wraps /admin/plans + /admin/payments.
// docs/replan-v2.md R6′.

import { api } from './api';

export type Currency = 'QAR' | 'AED' | 'SAR' | 'KWD' | 'BHD' | 'OMR' | 'USD';
export type Interval = 'month' | 'year';

export interface AdminPrice {
  currency: Currency;
  interval: Interval;
  amountMinor: number;
  displayAmount: string;
}

export interface AdminPlan {
  id: string;
  slug: string;
  audience: 'family' | 'school';
  tier: string;
  nameKey: string;
  taglineKey: string | null;
  features: string[];
  isHighlighted: boolean;
  /** 🔴 null = UNLIMITED. Not zero, and not "unset" — see PlanLimits in the route. */
  maxKids: number | null;
  maxTeachers: number | null;
  maxStudents: number | null;
  /** 0 = no trial. Per-plan by owner ruling 2026-08-16. */
  trialDays: number;
  trialRequiresCard: boolean;
  status: string;
  prices: AdminPrice[];
}

/**
 * Every field `PATCH /admin/plans/:id` accepts.
 *
 * 🔴 An ABSENT key means "leave unchanged"; an explicit `null` on a limit
 * means "unlimited". The two are different requests, which is why this is a
 * hand-written type rather than `Partial<AdminPlan>` — Partial would let a
 * caller think `undefined` and `null` were interchangeable here.
 */
export interface PlanUpdateInput {
  slug: string;
  audience: 'family' | 'school';
  tier: string;
  nameKey: string;
  taglineKey: string | null;
  features: string[];
  isHighlighted: boolean;
  maxKids: number | null;
  maxTeachers: number | null;
  maxStudents: number | null;
  trialDays: number;
  trialRequiresCard: boolean;
  status: 'active' | 'archived';
}

export async function listPlans(signal?: AbortSignal): Promise<AdminPlan[]> {
  const { plans } = await api<{ plans: AdminPlan[] }>('/admin/plans', { signal });
  return plans;
}

export type PlanCreateInput = Pick<PlanUpdateInput, 'slug' | 'audience' | 'tier' | 'nameKey'> &
  Partial<PlanUpdateInput>;

export async function createPlan(input: PlanCreateInput): Promise<{ id: string }> {
  return api<{ id: string }>('/admin/plans', { method: 'POST', body: input });
}

/**
 * PATCH a plan. Send only the keys you mean to change — the API leaves the
 * rest alone. To make a limit unlimited, send it as an explicit `null`.
 */
export async function updatePlan(id: string, input: Partial<PlanUpdateInput>): Promise<void> {
  await api(`/admin/plans/${id}`, { method: 'PATCH', body: input });
}

export async function upsertPrice(planId: string, price: AdminPrice): Promise<void> {
  await api(`/admin/plans/${planId}/prices`, { method: 'PUT', body: price });
}

export async function archivePlan(id: string): Promise<void> {
  await api(`/admin/plans/${id}`, { method: 'DELETE' });
}

// ─── Payments ─────────────────────────────────────────────────────────────
//
// MOVED to `lib/payments.ts` (M7c): invoice list, invoice detail, refunds.
//
// 🔴 `formatMinor` USED TO LIVE HERE AND WAS WRONG. It computed the 3-digit
// divisor for KWD/BHD/OMR and then formatted with `.toFixed(2)` anyway, so
// 1.234 KWD rendered as "1.23" — a silently dropped digit of money. It is
// deleted rather than fixed in place so nothing can import it again; use
// `lib/money.ts` (and the <Money> component) for every amount.
//
// The old `refundInvoice(invoiceId)` is gone too: a refund now REQUIRES a
// reason and supports partial amounts. See lib/payments.ts.
